import React, { useState, useEffect } from 'react';
import './Notification.css';
import iconBack from '../assets/icon-back-2.svg';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import NoticeDetail from './mypage/NoticeDetail';

interface NotificationProps {
  onBack: () => void;
}

interface NotificationItem {
  id: string;
  notification_type: string;
  title: string;
  content: string;
  image_url?: string;
  created_at: string;
  is_read: boolean;
  read_at?: string;
  read_id?: string; // notification_reads의 id
  source: 'notification_reads' | 'scheduled_pushes'; // 출처 구분
}

const Notification: React.FC<NotificationProps> = ({ onBack }) => {
  const { customer } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);

  // 알림 데이터 로드
  useEffect(() => {
    if (!customer) {
      console.log('customer가 없습니다');
      setIsLoading(false);
      return;
    }

    const loadNotifications = async () => {
      try {
        console.log('알림 조회 시작 - customer_id:', customer.id);

        // 1. notification_reads와 notifications를 JOIN해서 가져오기
        const { data: readsData, error: readsError } = await supabase
          .from('notification_reads')
          .select(`
            id,
            notification_id,
            is_read,
            read_at,
            received_at,
            notifications (
              title,
              content,
              notification_type,
              image_url,
              created_at
            )
          `)
          .eq('customer_id', customer.id)
          .order('received_at', { ascending: false });

        if (readsError) {
          console.error('notification_reads 조회 실패:', readsError);
        }

        // 2. scheduled_pushes에서 user_ids에 현재 사용자가 포함된 알림 가져오기
        const { data: scheduledData, error: scheduledError } = await supabase
          .from('scheduled_pushes')
          .select('*')
          .eq('status', 'sent')
          .contains('user_ids', [customer.id])
          .order('sent_at', { ascending: false });

        if (scheduledError) {
          console.error('scheduled_pushes 조회 실패:', scheduledError);
        }

        console.log('notification_reads 데이터:', readsData?.length);
        console.log('scheduled_pushes 데이터:', scheduledData?.length);

        // notification_reads 데이터 변환
        const formattedReads = readsData?.map((item: any) => ({
          id: item.id,
          notification_id: item.notification_id,
          notification_type: item.notifications?.notification_type || 'general',
          title: item.notifications?.title || '',
          content: item.notifications?.content || '',
          image_url: item.notifications?.image_url,
          created_at: item.notifications?.created_at || item.received_at,
          is_read: item.is_read,
          read_at: item.read_at,
          source: 'notification_reads' as const
        })) || [];

        // scheduled_pushes 데이터 변환
        const formattedScheduled = scheduledData?.map((item: any) => ({
          id: `scheduled_${item.id}`,
          notification_type: 'scheduled',
          title: item.title || '',
          content: item.body || '',
          created_at: item.sent_at || item.created_at,
          is_read: item.read_user_ids?.includes(customer.id) || false,
          source: 'scheduled_pushes' as const
        })) || [];

        // 두 배열 합치고 날짜순 정렬
        const allNotifications = [...formattedReads, ...formattedScheduled].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        console.log('전체 알림:', allNotifications.length);
        setNotifications(allNotifications);
      } catch (error) {
        console.error('알림 로드 오류:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();

    // Supabase Realtime 구독 - notification_reads 테이블 변경사항 감지
    const channel = supabase
      .channel('notification_reads_page_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_reads',
          filter: `customer_id=eq.${customer.id}`
        },
        (payload) => {
          console.log('알림 페이지 - notification_reads 변경 감지:', payload);
          // 새 알림이 추가되거나 변경되면 목록 다시 로드
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [customer]);

  // 시간 포맷팅
  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) return '방금 전';
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
    if (diffInHours < 24) return `${diffInHours}시간 전`;
    if (diffInDays < 7) return `${diffInDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'general':
        return '공지';
      case 'event':
        return '이벤트';
      case 'system':
        return '시스템';
      case 'promotion':
        return '프로모션';
      case 'scheduled':
        return '알림';
      default:
        return '알림';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'general':
        return '#beae79';
      case 'event':
        return '#ff6b6b';
      case 'system':
        return '#4dabf7';
      case 'promotion':
        return '#51cf66';
      case 'scheduled':
        return '#ffa94d';
      default:
        return '#beae79';
    }
  };

  const handleNotificationClick = async (notif: NotificationItem) => {
    // UI 즉시 업데이트
    setNotifications(notifications.map(n =>
      n.id === notif.id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
    ));

    // 출처에 따라 DB 읽음 처리
    if (notif.source === 'notification_reads') {
      try {
        await supabase
          .from('notification_reads')
          .update({
            is_read: true,
            read_at: new Date().toISOString()
          })
          .eq('id', notif.id);
      } catch (error) {
        console.error('읽음 처리 실패:', error);
      }
    } else if (notif.source === 'scheduled_pushes' && customer) {
      try {
        // scheduled_pushes의 read_user_ids 배열에 사용자 ID 추가
        const pushId = notif.id.replace('scheduled_', '');

        // 현재 read_user_ids 가져오기
        const { data: currentData } = await supabase
          .from('scheduled_pushes')
          .select('read_user_ids')
          .eq('id', pushId)
          .single();

        const currentReadUserIds = currentData?.read_user_ids || [];

        // 이미 읽음 처리된 경우 스킵
        if (!currentReadUserIds.includes(customer.id)) {
          await supabase
            .from('scheduled_pushes')
            .update({
              read_user_ids: [...currentReadUserIds, customer.id]
            })
            .eq('id', pushId);
        }
      } catch (error) {
        console.error('scheduled_pushes 읽음 처리 실패:', error);
      }
    }

    // 상세 화면으로 이동
    setSelectedNotification(notif);
  };

  // 상세보기에서 뒤로가기
  const handleBackFromDetail = () => {
    setSelectedNotification(null);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // 상세보기 화면
  if (selectedNotification) {
    return (
      <NoticeDetail
        notice={{
          id: selectedNotification.id,
          title: selectedNotification.title,
          content: selectedNotification.content,
          image_url: selectedNotification.image_url,
          created_at: selectedNotification.created_at,
          notification_type: selectedNotification.notification_type
        }}
        onBack={handleBackFromDetail}
        isEvent={selectedNotification.notification_type === 'promotion'}
      />
    );
  }

  return (
    <div className="notification-container">
      <div className="notification-header">
        <div className="notification-header-wrapper">
          <img src={iconBack} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="notification-title">알림</h1>
          <div className="header-icon"></div>
        </div>
      </div>

      <div className="notification-content">
        <h2 className="notification-page-title">알림</h2>
        {unreadCount > 0 && (
          <div className="notification-unread-info">
            새 알림 {unreadCount}개
          </div>
        )}

        {isLoading ? (
          <div className="notification-loading">
            <p>알림을 불러오는 중...</p>
          </div>
        ) : (
          <>
            <div className="notification-list">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`notification-item ${!notif.is_read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="notification-item-header">
                    <span
                      className="notification-type-badge"
                      style={{ backgroundColor: getTypeColor(notif.notification_type) }}
                    >
                      {getTypeLabel(notif.notification_type)}
                    </span>
                    <span className="notification-time">{getTimeAgo(notif.created_at)}</span>
                  </div>
                  <h3 className="notification-item-title">{notif.title}</h3>
                  <p className="notification-item-message">{notif.content}</p>
                </div>
              ))}
            </div>

            {notifications.length === 0 && (
              <div className="notification-empty">
                <p>알림이 없습니다.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Notification;
